import { Async, type AsyncState } from '../../platform/async/Async'
import { Skeleton } from '../../platform/async/Skeleton'
import { Panel } from '../../platform/primitives/Panel'
import type { Account } from '../accounts/useAccounts'
import type { CategoryListStatus } from '../categories/setCategoryLabel'
import { dayGroups } from '../transactions/dayGroups'
import { PAGE_SIZE } from '../transactions/filters'
import { Pager } from '../transactions/Pager'
import { TransactionCards } from '../transactions/TransactionCards'
import { TransactionGrid } from '../transactions/TransactionGrid'
import type { TxnPage } from '../transactions/useTransactions'
import './budget.css'

type Props = {
  state: AsyncState<TxnPage> & { fetching?: boolean }
  /** "Transactions counted this period". */
  subtitle: string
  /** "No spending in this envelope this month yet." */
  emptyText: string
  accounts: ReadonlyMap<string, Pick<Account, 'name' | 'mask'>>
  categoryNames: ReadonlyMap<string, string>
  categoriesStatus: CategoryListStatus
  /** Where a row's Back returns to: this page. */
  backFrom: string
  phone: boolean
  onPage: (n: number) => void
}

/**
 * The rows behind the envelope's spent figure, listed and never summed. The day bands carry no
 * subtotal (`plain`): M6's bands add money up in the browser, which this page must not.
 * A nested `<Async surface="transactions">` (live: four states).
 */
export function EnvelopeTransactionList({ state, subtitle, emptyText, accounts, categoryNames, categoriesStatus, backFrom, phone, onPage }: Props) {
  const listState: AsyncState<TxnPage> =
    state.status === 'success' && state.data.total === 0 ? { status: 'empty' } : state
  return (
    <Panel className={`txn-panel bd-list${phone ? ' txn-panel--flow' : ''}`}>
      <div className="bd-list__head">
        <h3 className="bd-list__title">Transactions</h3>
        <p className="bd-list__sub">{subtitle}</p>
      </div>
      <Async
        surface="transactions"
        state={listState}
        skeleton={
          <div className="txn-skeleton" aria-hidden="true">
            <Skeleton height={44} />
            <Skeleton height={44} />
            <Skeleton height={44} />
          </div>
        }
        empty={{ title: emptyText }}
      >
        {(page) => {
          const groups = dayGroups(page.rows, {})
          return (
            <>
              {phone && page.rows.length > 0 ? (
                <TransactionCards groups={groups} accounts={accounts} categories={categoryNames} categoriesStatus={categoriesStatus} backFrom={backFrom} plain />
              ) : (
                <div className="txn-panel__grid">
                  <TransactionGrid
                    groups={groups}
                    accounts={accounts}
                    categories={categoryNames}
                    categoriesStatus={categoriesStatus}
                    backFrom={backFrom}
                    plain
                    empty={<p className="txn-empty">{emptyText}</p>}
                  />
                </div>
              )}
              {(page.hasMore || page.page > 1) && <Pager page={page} fetching={state.fetching ?? false} onPage={onPage} pageSize={PAGE_SIZE} />}
            </>
          )
        }}
      </Async>
    </Panel>
  )
}
